/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import { useRouter } from 'next/navigation';
import useExclusiveRewardsModal from '../../hooks/useExclusiveRewardsModal';
import { ATTR_NAMES, getAttrFromProduct } from '../../types/attrNames';
import { CmsProduct } from '../../types/cmsProduct';
import { CarInsurance } from '../../types/cmsTypes';
import { getUtmParams } from '../../utils/carInsurance/getUtmParams';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { useBreakpoint } from '../../utils/useBreakpoint';
import { useSafeSearchParams } from '../../utils/useSafeSearchParams';
import {
  matchApplyV3ModalIntent,
  resolveCtaUrlForVertical,
  useApplyV3Modal,
} from '../ApplyCta';
import Button, { ButtonSize, ButtonStyleType } from '../Button';
import { MaterialIcon } from '../MaterialIcon';
import { useCentralModals } from './CentralModalCI/useSuccessErrorModal';
import { useSafeCarInsuranceContext } from './context/useSafeCarInsurance';

export interface CarInsuranceCtaProps {
  className?: string;
  showPriceCheck: boolean;
  carInsuranceSection: CarInsurance | undefined;
  buttonStyleMap: Record<string, ButtonStyleType>;
  sendAnalytics: () => void;
  ctaLinkOverride: string | undefined;
  ctaLabelOverride: string | undefined;
  product: CmsProduct;
  staticText: StaticText;
}

export type StaticText = {
  apply_now: string;
  check_price: string;
  buy_now: string;
};

export interface CarInsuranceCtaBuilderProps {
  showPriceCheck: boolean;
  carInsuranceSection: CarInsurance | undefined;
  buttonStyleMap: Record<string, ButtonStyleType>;
  ctaLinkOverride: string | undefined;
  ctaLabelOverride: string | undefined;
}

export interface CarInsuranceCtaBuilderInstanceProps {
  className?: string;
  product: CmsProduct;
  sendAnalytics: () => void;
  staticText: StaticText;
}

export const CarInsuranceCtaBuilder = (
  props: CarInsuranceCtaBuilderProps,
): CarInsuranceCtaBuilderInstance => {
  const Component = (instanceProps: CarInsuranceCtaBuilderInstanceProps) => {
    return <CarInsuranceCta {...props} {...instanceProps} />;
  };
  Component.displayName = 'CarInsuranceCtaBuilder';

  return Component;
};

export type CarInsuranceCtaBuilderInstance = (
  props: CarInsuranceCtaBuilderInstanceProps,
) => React.ReactNode;

const CarInsuranceCta = (props: CarInsuranceCtaProps) => {
  const {
    className,
    showPriceCheck,
    carInsuranceSection,
    buttonStyleMap,
    sendAnalytics,
    staticText,
    ctaLinkOverride,
    ctaLabelOverride,
    product,
  } = props;

  const { openErrorModal } = useCentralModals();
  const searchParams = useSafeSearchParams();
  const router = useRouter();
  const tracking = {
    utm_source: searchParams?.get('utm_source') ?? '',
    utm_medium: searchParams?.get('utm_medium') ?? '',
    utm_campaign: searchParams?.get('utm_campaign') ?? '',
    utm_channel: searchParams?.get('utm_channel') ?? '',
    utm_content: searchParams?.get('utm_content') ?? '',
    utm_strategy: searchParams?.get('utm_strategy') ?? '',
    utm_term: searchParams?.get('utm_term') ?? '',
  };
  const { brand, langPrefix } = useCmsClientContext();
  // Check if product has REWARD_PRODUCT_ID attribute
  const rewardProductIdAttr = getAttrFromProduct(
    ATTR_NAMES.REWARD_PRODUCT_ID,
    product,
  );
  const { buttonSize, buttonIconSize } = useBreakpoint({
    _: { buttonSize: 'small' as ButtonSize, buttonIconSize: 2 },
    md: { buttonSize: 'regular' as ButtonSize, buttonIconSize: 3 },
  });

  const hasRewardProductId = Boolean(rewardProductIdAttr?.value);
  const { openModal: openExclusiveRewardsModal } = useExclusiveRewardsModal();
  const { open: openApplyV3Modal } = useApplyV3Modal();
  const carInsuranceContext = useSafeCarInsuranceContext();
  if (!carInsuranceContext) {
    return null;
  }

  const productName = product.title ?? '';
  const providerName = product.provider?.provider_name ?? '';

  const {
    requestParams: {
      user: {
        value: { firstName, lastName, email, phone },
      },
      vehicle: {
        value: { type, makeName, modelName, fairMarket, manufactureYear, trim },
      },
      prices: {
        value: { propertyDamage, personalAccident, isActsOfNature },
      },
    },
    sendLeadToZoho,
    getCarInsuranceLayoutId,
  } = carInsuranceContext;

  const applyButtonClick = async () => {
    sendAnalytics();
    if (brand === 'singsaver') {
      // If product has REWARD_PRODUCT_ID, open ExclusiveRewardsModal instead
      if (hasRewardProductId && product?.uid) {
        openExclusiveRewardsModal(product.uid);
        return;
      }
      // Call the resolver directly (not via `useCtaUrl`): the hook reads
      // `vertical` from page context and does not allow a per-call override,
      // but here we must force `CAR_INSURANCE` regardless of the page the CTA
      // is rendered on. Inside an imperative click handler anyway, so there is
      // no `<ApplyCtaLink>` to delegate to.
      const redirectUrl = resolveCtaUrlForVertical({
        vertical: 'CAR_INSURANCE',
        product,
        ctaLinkOverride,
        brand,
        langPrefix,
      });

      const modalIntent = matchApplyV3ModalIntent(redirectUrl);
      if (modalIntent) {
        openApplyV3Modal(modalIntent);
        return;
      }

      window.open(redirectUrl, '_blank');

      return;
    }
    if (brand === 'moneymax') {
      // CORE-4767: read inside the handler, not during render — `getUtmParams`
      // falls back to the session attribution cookie, which the server cannot
      // see. Reading it in the render body would desync SSR from hydration.
      const { clkid, clktype } = getUtmParams(searchParams);
      try {
        await sendLeadToZoho({
          First_Name: firstName || '-',
          Last_Name: lastName || '-',
          Email: email,
          Phone: phone,
          Product_Type: 'Motor Vehicle',
          Vehicle_Type: type,
          Car_Make: makeName,
          Car_Model: modelName,
          Car_Year: manufactureYear.toString(),
          Car_Variant: trim,
          Bodily_Injury: String(propertyDamage),
          Property_Damage: String(propertyDamage),
          Personal_Accident: String(personalAccident),
          Acts_of_Nature_Coverage:
            typeof isActsOfNature === 'undefined'
              ? null
              : isActsOfNature
                ? 'With AON'
                : 'Without AON',
          FMV: String(fairMarket),
          UtmSource: tracking.utm_source,
          UtmMedium: tracking.utm_medium,
          UtmCampaign: tracking.utm_campaign,
          UtmChannel: tracking.utm_channel,
          UtmContent: tracking.utm_content,
          UtmStrategy: tracking.utm_strategy,
          UtmTerm: tracking.utm_term,
          scaleo_click_id: clkid,
          scaleo_click_type: clktype,
          isAutoCreated: true,
          Lead_Status: 'New Lead',
          Layout: {
            id: await getCarInsuranceLayoutId(),
          },
          Selected_Product: productName ?? '',
          Selected_Provider: providerName ?? '',
        });
        router.push('/car-insurance/application-success');
      } catch {
        openErrorModal();
      }
    }
  };

  return (
    <Button
      endIcon={
        <MaterialIcon
          icon={showPriceCheck ? 'open_in_new' : 'arrow_right_alt'}
          className="w-full"
          size={buttonIconSize}
        />
      }
      size={buttonSize}
      styleType={
        showPriceCheck
          ? carInsuranceSection?.buttonCheckType
            ? buttonStyleMap[carInsuranceSection.buttonCheckType]
            : 'primary'
          : carInsuranceSection?.buttonBuyType
            ? buttonStyleMap[carInsuranceSection.buttonBuyType]
            : 'cta'
      }
      onClick={applyButtonClick}
      className={classNames(className)}
    >
      {showPriceCheck
        ? (carInsuranceSection?.buttonCheckText ?? staticText.check_price)
        : (ctaLabelOverride ??
          carInsuranceSection?.buttonBuyText ??
          staticText.buy_now)}
    </Button>
  );
};
